/* Gerber: restyles the Custom Price Calculator app on product pages.
   - Dropdowns become button options (a radio list in the buy box layout).
   - Buy box layout: price, quantity, Add to cart and any quote button, lead time and help note blocks move into a right-hand box.
   The app keeps owning the real <select> elements and the price; this script only mirrors them
   (sets select.selectedIndex + dispatches "change") and moves existing nodes, so pricing and cart properties are untouched.
   Loaded only when the product section setting "Enhanced calculator" is on. */
(() => {
  const MAX_PILLS = 12;
  let qtySel = null;

  const fire = sel => sel.dispatchEvent(new Event('change', { bubbles: true }));
  const labelOf = opt => (opt.dataset.label || opt.textContent).trim();

  function buildPills(sel) {
    const wrap = document.createElement('div');
    wrap.className = 'gce-pills';
    wrap.setAttribute('role', 'radiogroup');
    const label = sel.closest('.element')?.querySelector('label');
    if (label) wrap.setAttribute('aria-label', label.textContent.trim());
    [...sel.options].forEach((opt, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gce-pill';
      b.setAttribute('role', 'radio');
      b.textContent = labelOf(opt);
      b.addEventListener('click', () => {
        sel.selectedIndex = i;
        fire(sel);
      });
      wrap.appendChild(b);
    });
    const sync = () => {
      [...wrap.children].forEach((b, i) => {
        const on = i === sel.selectedIndex;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.classList.toggle('is-active', on);
      });
    };
    sel.addEventListener('change', sync);
    sel.classList.add('gce-native');
    sel.after(wrap);
    sync();
  }

  // Buy box: moves the app's price, the quantity field and the theme's own buy blocks into one right-hand column.
  // When the calculator lives inside the cart <form>, the box is built inside that form (host), so every
  // field stays a form member and the order properties are unchanged. Otherwise the host is <product-info>.
  function ensurePanel(host) {
    let panel = host.querySelector(':scope > .gce-panel');
    if (!panel) {
      panel = document.createElement('aside');
      panel.className = 'gce-panel';
      panel.setAttribute('aria-label', 'Order');
      host.classList.add('gce-host');
      host.appendChild(panel);
    }
    return panel;
  }

  function adopt(info, host, panel, qtyEl) {
    const formMode = host !== info;
    const order = [
      info.querySelector('.price-marker'),
      qtyEl,
      formMode ? info.querySelector('.product-form__buttons') : info.querySelector('product-form'),
      info.querySelector('.gce-quote'),
      info.querySelector('.product__lead-time'),
      info.querySelector('.gce-help')
    ];
    // Only touch the DOM when the order is wrong, so the MutationObserver does not loop
    const want = order.filter(Boolean);
    const have = [...panel.children];
    if (want.length !== have.length || want.some((n, i) => n !== have[i])) want.forEach(n => panel.appendChild(n));
    const rows = [...host.children].filter(c => c !== panel && getComputedStyle(c).display !== 'none').length;
    panel.style.setProperty('--gce-rows', Math.max(rows, 1));
  }

  // Keeps the buy box in view while the options and the tabs below scroll past. CSS sticky cannot do this because the
  // box lives inside the options container, which is only as tall as the box. The box is moved with a transform,
  // limited to the product section and the tabs (or to the product section alone when it would overlap the tabs).
  const DESKTOP = window.matchMedia('(min-width: 1200px)');
  let stickyPanel = null;
  let ticking = false;

  function followScroll() {
    ticking = false;
    const panel = stickyPanel;
    if (!panel) return;
    if (!DESKTOP.matches) { panel.style.transform = ''; return; }
    const section = panel.closest('.gerber-product');
    if (!section) return;
    const current = parseFloat((panel.style.transform.match(/translate3d\(0px, ([-\d.]+)px/) || [])[1]) || 0;
    const rect = panel.getBoundingClientRect();
    const natural = rect.top + window.scrollY - current;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--gh-sticky-h')) || 0;
    const tabs = document.querySelector('.gpt__inner');
    const tabsRect = tabs ? tabs.getBoundingClientRect() : null;
    const overlaps = tabsRect && rect.left < tabsRect.right;
    const limitEl = tabs && !overlaps ? tabs : section;
    const limit = limitEl.getBoundingClientRect().bottom + window.scrollY;
    const want = window.scrollY + header + 24 - natural;
    const max = Math.max(0, limit - panel.offsetHeight - natural);
    const next = Math.min(Math.max(want, 0), max);
    panel.style.transform = next > 0 ? 'translate3d(0px, ' + Math.round(next) + 'px, 0)' : '';
  }

  function requestFollow() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(followScroll);
  }

  function startFollowing(panel) {
    if (stickyPanel) return;
    stickyPanel = panel;
    window.addEventListener('scroll', requestFollow, { passive: true });
    window.addEventListener('resize', requestFollow);
    new ResizeObserver(requestFollow).observe(document.body);
    requestFollow();
  }

  // Product metafield custom.hidden_options ("Field name | Option label" per line) removes calculator options
  // that a product should not offer. The option is removed from the real <select> too, so it cannot be chosen.
  function hideOptions(calc) {
    const raw = calc.closest('.gerber-product')?.dataset.hiddenOptions || '';
    raw.split('~').forEach(line => {
      const [field, option] = line.split('|').map(x => (x || '').trim().toLowerCase());
      if (!field || !option) return;
      calc.querySelectorAll('.element').forEach(el => {
        if ((el.dataset.element || '').trim().toLowerCase() !== field) return;
        const sel = el.querySelector('select.calc-prop');
        if (!sel) return;
        [...sel.options].forEach(opt => {
          if (labelOf(opt).toLowerCase() === option) opt.remove();
        });
        if (sel.selectedIndex < 0) sel.selectedIndex = 0;
      });
    });
  }

  // Plain-variant products (no calculator): the theme's own price, quantity, Add to cart, lead time and help blocks
  // move into the same right-hand box, and the variant buttons are styled as radio rows by the stylesheet.
  function layoutPlain(root) {
    const info = root.querySelector('product-info');
    if (!info) return;
    let panel = info.querySelector(':scope > .gce-panel');
    if (!panel) {
      panel = document.createElement('aside');
      panel.className = 'gce-panel';
      panel.setAttribute('aria-label', 'Order');
      info.classList.add('gce-host');
      const anchor = info.querySelector(':scope > variant-radios, :scope > variant-selects');
      const price = info.querySelector('div[id^="price-"]');
      if (anchor) anchor.after(panel); else if (price) price.before(panel); else info.appendChild(panel);
    }
    const want = [
      info.querySelector('div[id^="price-"]'),
      info.querySelector('.product-form__quantity'),
      info.querySelector('product-form'),
      info.querySelector('.product__lead-time'),
      info.querySelector('.gce-help')
    ].filter(Boolean);
    const have = [...panel.children];
    if (want.length !== have.length || want.some((n, i) => n !== have[i])) want.forEach(n => panel.appendChild(n));
    const rows = [...info.children].filter(ch => ch !== panel && getComputedStyle(ch).display !== 'none').length;
    panel.style.setProperty('--gce-rows', Math.max(rows, 1));
    sizeFirst(info);
    startFollowing(panel);
  }

  // Products with a Size option and one other option (for example Material): Size is shown first and the other
  // option lists only what exists for the chosen size. The theme's picker reads the groups in page order, so the
  // order is changed visually (CSS order) and the variants are read from the picker's own JSON data.
  function sizeFirst(info) {
    const radios = info.querySelector('variant-radios');
    if (!radios || radios.dataset.gceSize) return;
    const fieldsets = [...radios.querySelectorAll('fieldset')];
    if (fieldsets.length !== 2) return;
    const sizeAt = fieldsets.findIndex(f => /size/i.test(f.querySelector('legend')?.textContent || ''));
    if (sizeAt < 0) return;
    const otherAt = 1 - sizeAt;
    let variants;
    try { variants = JSON.parse(radios.querySelector('script[type="application/json"]').textContent); } catch (e) { return; }
    radios.dataset.gceSize = '1';
    fieldsets[sizeAt].style.order = '-1';
    // Sizes from smallest to largest, whatever order they were entered in
    const sizeSet = fieldsets[sizeAt];
    sizeSet.style.display = 'flex';
    sizeSet.style.flexDirection = 'column';
    const sizeKey = v => (v.match(/[\d.]+/g) || []).map(Number);
    [...sizeSet.querySelectorAll('input')]
      .sort((a, b) => { const x = sizeKey(a.value), y = sizeKey(b.value); return (x[0] - y[0]) || ((x[1] || 0) - (y[1] || 0)); })
      .forEach((input, i) => {
        input.style.order = String(i * 2 + 1);
        if (input.nextElementSibling) input.nextElementSibling.style.order = String(i * 2 + 2);
      });
    const checked = f => f.querySelector('input:checked')?.value;
    const exists = (size, other) => variants.some(v => v.options[sizeAt] === size && v.options[otherAt] === other);

    const refresh = () => {
      const size = checked(fieldsets[sizeAt]);
      fieldsets[sizeAt].querySelectorAll('input').forEach(i => i.classList.remove('disabled'));
      fieldsets[otherAt].querySelectorAll('input').forEach(input => {
        const label = input.nextElementSibling;
        if (label) label.style.display = exists(size, input.value) ? '' : 'none';
        input.classList.remove('disabled');
      });
    };

    // Before the theme reads the choices: if the current material does not exist for the new size, switch to one that does
    radios.addEventListener('change', e => {
      if (!fieldsets[sizeAt].contains(e.target)) return;
      const other = checked(fieldsets[otherAt]);
      if (exists(e.target.value, other)) return;
      const first = [...fieldsets[otherAt].querySelectorAll('input')].find(i => exists(e.target.value, i.value));
      if (first) first.checked = true;
    }, true);
    // After the theme has updated its own state
    radios.addEventListener('change', () => requestAnimationFrame(refresh));
    refresh();
  }

  // Descriptions typed in the Shopify editor often end with blank lines (a lone &nbsp;). Hide blocks with no content
  // and drop the outer margins so every product description starts and ends flush with the page's own spacing.
  function cleanDescription() {
    const d = document.querySelector('.gerber-product .product__description');
    if (!d || d.dataset.gceClean) return;
    d.dataset.gceClean = '1';
    [...d.children].forEach(el => {
      const empty = !el.textContent.replace(/[\s\u00a0]/g, '') && !el.querySelector('img, iframe, video, svg') && !el.id;
      if (empty) el.hidden = true;
    });
    const shown = [...d.children].filter(el => !el.hidden && getComputedStyle(el).display !== 'none');
    if (shown.length) {
      shown[0].style.marginTop = '0';
      shown[shown.length - 1].style.marginBottom = '0';
    }
  }

  function enhance(calc) {
    const selects = [...calc.querySelectorAll('.element select.calc-prop')];
    // The app builds #calculator in stages; wait until it has fields (not only dropdowns: some calculators use inputs only)
    if (!calc.querySelector('.element, .price-marker') && !qtySel) return;
    const layout = !!calc.closest('.gce-layout');
    const info = calc.closest('product-info');
    const host = info && (calc.closest('form') || info);
    // Once moved into the buy box the quantity select is no longer inside #calculator, so it is remembered
    qtySel = qtySel || selects.find(s => /how many|quantity|qty/i.test(s.closest('.element')?.dataset.element || ''));

    if (!calc.dataset.gce) {
      calc.dataset.gce = '1';
      hideOptions(calc);
      selects.forEach(sel => {
        if (layout && sel === qtySel) { sel.closest('.element').classList.add('gce-qty'); return; }
        if (sel.options.length <= MAX_PILLS) buildPills(sel);
      });
      if (layout && host) ensurePanel(host);
    }
    if (layout && host) adopt(info, host, ensurePanel(host), qtySel && qtySel.closest('.element'));
    if (layout && host) startFollowing(ensurePanel(host));
  }

  const run = () => {
    cleanDescription();
    const plain = document.querySelector('.gerber-product.gce-plain');
    if (plain) layoutPlain(plain);
    const calc = document.querySelector('#calculator');
    if (calc) enhance(calc);
  };
  run();
  new MutationObserver(run).observe(document.body, { childList: true, subtree: true });
})();
